/**
 * SSE(Server-Sent Events) 프레임 파서.
 *
 * `/stream/claude`는 POST라 브라우저 EventSource(GET 전용)를 쓸 수 없어
 * fetch + ReadableStream을 직접 파싱한다. 청크는 프레임 경계와 무관하게
 * 쪼개져 도착하므로 누적 버퍼가 필수다.
 *
 * 주의: `data:` 값의 선행 공백을 제거하지 않는다. 백엔드(Spring SseEmitter)는
 * 콜론 뒤에 공백을 넣지 않으므로, 공백을 벗기면 답변 본문의 실제 공백이 사라진다.
 */

export type SseFrame = {
  /** 이름 없는 이벤트는 'message' */
  event: string;
  data: string;
};

const FRAME_SEPARATOR = '\n\n';

function stripCarriageReturn(line: string): string {
  return line.endsWith('\r') ? line.slice(0, -1) : line;
}

function parseFrame(raw: string): SseFrame | null {
  let event = 'message';
  const dataLines: string[] = [];

  for (const rawLine of raw.split('\n')) {
    const line = stripCarriageReturn(rawLine);

    // 빈 줄과 주석(`:`로 시작)은 무시
    if (line === '' || line.startsWith(':')) continue;

    const colon = line.indexOf(':');
    const field = colon === -1 ? line : line.slice(0, colon);
    const value = colon === -1 ? '' : line.slice(colon + 1);

    if (field === 'event') {
      event = value;
    } else if (field === 'data') {
      dataLines.push(value);
    }
    // id, retry 등 나머지 필드는 사용하지 않는다
  }

  if (dataLines.length === 0) return null;

  return { event, data: dataLines.join('\n') };
}

export type SseParser = {
  /** 디코딩된 청크를 밀어넣고, 이번에 완성된 프레임들을 받는다 */
  push: (chunk: string) => SseFrame[];
  /** 스트림 종료 시 호출. 종결자 없이 남은 잔여 버퍼를 프레임으로 만든다 */
  flush: () => SseFrame[];
};

export function createSseParser(): SseParser {
  let buffer = '';

  return {
    push(chunk: string): SseFrame[] {
      buffer += chunk;

      const frames: SseFrame[] = [];
      let separator = buffer.indexOf(FRAME_SEPARATOR);

      while (separator !== -1) {
        const raw = buffer.slice(0, separator);
        buffer = buffer.slice(separator + FRAME_SEPARATOR.length);

        const frame = parseFrame(raw);
        if (frame) frames.push(frame);

        separator = buffer.indexOf(FRAME_SEPARATOR);
      }

      return frames;
    },

    flush(): SseFrame[] {
      if (buffer.trim() === '') {
        buffer = '';
        return [];
      }

      const frame = parseFrame(buffer);
      buffer = '';

      return frame ? [frame] : [];
    },
  };
}
